import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AbstractBaseStyleFieldComponent } from '../../BaseStyleField/abstract-base-style-field.component';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { SocialLinkStyleModel } from '../../../models/link-style.model';
import { DropdownFieldModel } from '../../../../../Common/PageBuilder/models/dropdown-field.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { StylesService } from '../../../../../Common/Styles/services/styles.service';
import { AbstractPageBuilderService } from '../../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { Style } from '../../../../../Common/Styles/models/style.model';
import { StyleStateEnum } from '../../../../../Common/PageBuilder/enums/style-state.enum';
import { LinkLayoutOptionEnum } from '../../../../../Common/PageBuilder/enums/link-layout-option.enum';
import { LinksStyleFieldModel } from '../../../../../Common/PageBuilder/models/links-style-field.model';
import { TextStyleFieldModel } from '../../../../../Common/PageBuilder/models/text-style-field.model';
import { TextStyleDefaultsModel } from '../../../../../Common/PageBuilder/models/text-style-defaults.model';
import { FontAlignmentEnum } from '../../../../../Common/Forms/enums/font-alignment.enum';
import { RadioTypesEnum } from '../../../../../Common/Forms/enums/radio-types.enum';
import { ColorPickerFieldModel } from '../../../../../Common/PageBuilder/models/color-picker-field.model';
import { LinkDisplayOptionEnum } from '../../../../../Common/PageBuilder/enums/link-display-option.enum';
import { textStyleFontDropdownOptions } from '../../../models/text-style-font-dropdown-options.model';
import { textStyleFontSizeDropdownOptions } from '../../../models/text-style-font-size-dropdown-options.model';
import { CheckboxTypesEnum } from '../../../../../Common/Forms/enums/checkbox-types.enum';
import { ManageStylesDialogComponent } from '../../../../../Common/Styles/components/ManageStylesDialog/manage-styles-dialog.component';
import { Template } from '../../../../../Common/Styles/models/template.model';
import { SaveTwigTemplateDialogComponent } from '../../../../../Common/Dialogs/components/SaveTwigTemplateDialog/save-twig-template-dialog.component';
import { SaveStylesDialogComponent } from '../../../../../Common/Dialogs/components/SaveStylesDialog/save-styles-dialog.component';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { BlockDisplayTypeEnum } from '../../../enums/block-display-type.enum';
import { AdvancedColorPickerFieldModel, backgroundColorConfig, textColorConfig, iconBackgroundColorConfig} from '@Common/PageBuilder/models/advanced-color-picker-field.model';


@Component({
    selector: 'agpr-links-style-field',
    templateUrl: './links-style-field.component.html',
    styleUrls: ['./links-style-field.component.scss'],
    standalone: false
})
export class LinksStyleFieldComponent extends AbstractBaseStyleFieldComponent implements OnInit, OnDestroy {

    @Input() declare value: SocialLinkStyleModel;
    @Input() declare field: LinksStyleFieldModel;
    @Input() textPrimaryLabel: boolean = false;

    styleStateField: DropdownFieldModel;
    displayDropDownField: DropdownFieldModel;
    iconSizeDropDownField: DropdownFieldModel;
    iconColorDropDownField: DropdownFieldModel;
    colorPickerField: AdvancedColorPickerFieldModel;
    fontColorPickerField: AdvancedColorPickerFieldModel;
    iconBackgroundColorField: AdvancedColorPickerFieldModel;
    fontFamilyField: DropdownFieldModel;
    fontSizeField: DropdownFieldModel;
    linkTextFontField = new TextStyleFieldModel(
        'linkTextStyle',
        false,
        new TextStyleDefaultsModel(
            '12px',
            '#5e6473',
            true,
            true
        ),
        null,
        null,
        false
    );


    protected defaultStyleStateOptions;
    protected styles: Style[];
    protected subscriptions: Subscription[] = [];

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly fontAlignmentEnum = FontAlignmentEnum;
    readonly radioTypesEnum = RadioTypesEnum;
    readonly linkLayoutOptionEnum = LinkLayoutOptionEnum;
    readonly checkboxTypesEnum = CheckboxTypesEnum;
    readonly iconBackgroundDefaultColors = {
        light: chartColors.agprBlack,
        dark: chartColors.agprWhite
    };

    constructor(
        protected coreDialogService: CoreDialogService,
        protected stylesService: StylesService,
        public abstractPageBuilderService: AbstractPageBuilderService
    ) {
        super(coreDialogService);
        this.subscriptions.push(
            this.stylesService.all$.pipe(
                filter(x => !!x)
            ).subscribe(styles => {
                this.styles = styles;

                this.updateStyleOptions();
                this.updateStyleFromSavedStyle();
            })
        );
    }

    ngOnInit() {
        if (this.block?.displayElement) {
            this.field.allowExpertMode = this.block?.displayElement?.displayType !== BlockDisplayTypeEnum.ONLINE;
        }

        if (!this.value) {
            this.value = new SocialLinkStyleModel();
        }

        this.defaultStyleStateOptions = this.field.defaultStyleStateOptions;
        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }
        this.styleStateField = new DropdownFieldModel(
            null,
            'displayStyleState',
            'PageBuilder.styles.contentBlockStyle',
            '',
            this.defaultStyleStateOptions
        );
        this.updateStyleOptions();

        this.displayDropDownField = new DropdownFieldModel(
            null,
            'display',
            undefined,
            undefined,
            [
                { label: 'PageBuilder.formField.styles.iconAndLinkText', value: LinkDisplayOptionEnum.iconAndLinkText },
                { label: 'PageBuilder.formField.styles.iconOnly', value: LinkDisplayOptionEnum.iconOnly },
                { label: 'PageBuilder.formField.styles.linkTextOnly', value: LinkDisplayOptionEnum.linkTextOnly },
            ]
        );

        this.iconSizeDropDownField = new DropdownFieldModel(
            null,
            'iconSize',
            undefined,
            undefined,
            [
                { label: 'PageBuilder.formField.styles.small', value: 'small' },
                { label: 'PageBuilder.formField.styles.medium', value: 'medium' },
                { label: 'PageBuilder.formField.styles.large', value: 'large' },
            ],
            'PageBuilder.formField.tooltip.iconSize'
        );

        this.iconColorDropDownField = new DropdownFieldModel(
            null,
            'iconColor',
            undefined,
            undefined,
            [
                { label: 'PageBuilder.formField.styles.light', value: 'light' },
                { label: 'PageBuilder.formField.styles.dark', value: 'dark' },
            ],
            'PageBuilder.formField.tooltip.iconStyle'
        );

        this.fontSizeField = new DropdownFieldModel(
            null,
            'fontSize',
            '',
            '',
            textStyleFontSizeDropdownOptions,
            'PageBuilder.formField.tooltip.fontSize'
        );

        this.fontFamilyField = new DropdownFieldModel(
            null,
            'fontFamily',
            '',
            '',
            textStyleFontDropdownOptions,
            'Common.forms.richText.fontFamily'
        );


        this.colorPickerField = AdvancedColorPickerFieldModel.create(backgroundColorConfig(true));


        this.fontColorPickerField = AdvancedColorPickerFieldModel.create(textColorConfig(true));



        this.iconBackgroundColorField = AdvancedColorPickerFieldModel.create(iconBackgroundColorConfig(true));
    }

    protected updateStyleOptions(): void {
        if (!this.styles || !this.value  || !this.defaultStyleStateOptions) {
            return;
        }

        if (this.field.changeDefaultStyleStateOptions) {
            this.defaultStyleStateOptions = this.field.changeDefaultStyleStateOptions(this);
        }

        const styleDropdownOptions = this.styles
            .filter(style => style.type === this.field.styleType)
            .map(
                style => {
                    return {
                        label: style.name,
                        value: style.id
                    };
                }
            );

        this.styleStateField.options = [
            ...this.defaultStyleStateOptions,
            ...styleDropdownOptions
        ];
    }

    saveExpertTemplate(){
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;
        this.styleModel.template = this.value.template;
        this.saveTwigTemplateDialogModel.inputs.defaultTemplate = this.abstractPageBuilderService.getDefaultTemplateByBlockType(
            this.abstractPageBuilderService.getBlockTypeEnumFromStyleTypeEnum(this.field.styleType),
            StyleStateEnum.DEFAULT
        );
        // Set the default template if there's not a template on this style already
        if (!this.styleModel.template) {
            this.setTemplate = true;
            this.styleModel.template = new Template();
            this.styleModel.template.template = this.saveTwigTemplateDialogModel.inputs.defaultTemplate;
        }

        this.saveTwigTemplateDialogModel.inputs.type = this.field.styleType;
        this.saveTwigTemplateDialogModel.inputs.inputStyle = this.styleModel;
        const dialogKey = this.coreDialogService.open(SaveTwigTemplateDialogComponent, this.saveTwigTemplateDialogModel);
    }

    saveStyle(){
        if (!this.styleModel) {
            this.styleModel = new Style();
        }

        this.styleModel.style = this.value;

        const io: DialogIoModel = {
            inputs: {
                type: this.field.styleType,
                inputStyle: this.styleModel
            },
            outputs: {
                dialogClose: (response) => {
                    if (response) {
                        this.setStyle(response, 'displayStyleState', true);
                    }
                }
            }
        };

        this.coreDialogService.open(SaveStylesDialogComponent, io);
    }

    manageStylesButtonClicked(){
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageStylesDialogComponent, io);
    }

    protected updateStyleFromSavedStyle() {
        if (!this.value || !this.styles || !Number(this.value.styleState)) {
            return;
        }

        const savedStyle: Style = this.styles.find(x => x.id === this.value.styleState as number);
        if (!savedStyle) {
            // the list doesn't contain the id we're looking for, can't load it
            return;
        }
        const linkStyleModel = new SocialLinkStyleModel(savedStyle.style);
        linkStyleModel.styleState = this.value.styleState;
        linkStyleModel.displayStyleState = this.value.displayStyleState;
        linkStyleModel.template = savedStyle.template;

        this.value = linkStyleModel;
    }

    /** Change the icon background color when icon color is set */
    onIconColorChange(iconColor: 'light' | 'dark') {
        if (this.value.iconBackgroundColor !== this.iconBackgroundDefaultColors.dark
            && this.value.iconBackgroundColor !== this.iconBackgroundDefaultColors.light) {
            // User has set a custom color, let's keep it
            return;
        }

        this.setStyle(this.iconBackgroundDefaultColors[iconColor], this.iconBackgroundColorField.property);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
